'use client'

import Link from 'next/link'
import useSWR from 'swr'
import { ArrowUpRight, RotateCw } from 'lucide-react'
import SimilarBuyNow from './buy-now'
import InscriptionContent from '@/components/inscription-content'
import useExchangeRate from '@/utils/hooks/useExchangeRate'
import type { SimilarItem } from '@/utils/similar-listings'
import styles from './similar-listings.module.css'

type Result = {
	collection: { slug: string; name?: string } | null
	listings: { item: SimilarItem; matches: number }[]
}

type Props = {
	inscription: SimilarItem
	/**
	 * `modal`: the grid sits under a purchase's success actions inside the
	 * 400px purchase modal, titled as an invitation to keep shopping; card
	 * links close the modal before navigating (`onNavigate`), and Buy now
	 * opens the next purchase in place.
	 */
	variant?: 'page' | 'modal'
	onNavigate?: () => void
}

export default function SimilarListings({ inscription, variant = 'page', onNavigate }: Props) {
	const { data, error, isLoading, mutate } = useSWR<Result>(
		inscription.collection?.slug ? `/api/similar-listings?id=${encodeURIComponent(inscription.id)}` : null,
		async (url: string) => {
			const response = await fetch(url)
			if (!response.ok) throw new Error('Similar listings unavailable')
			return response.json()
		},
		{ refreshInterval: 60000, errorRetryCount: 2 }
	)
	const { exchangeRate } = useExchangeRate()
	const collection = data?.collection || inscription.collection
	if (!collection?.slug || (data && !data.listings.length)) return null
	const collectionUrl = `/collection/${encodeURIComponent(collection.slug)}`
	const modal = variant === 'modal'
	return <section className={`${styles.root} ${modal ? styles.modal : ''}`} aria-label={modal ? 'Keep shopping' : 'Similar listings'} aria-busy={isLoading}>
		<div className={styles.header}>
			<div className="min-w-0">
				<h2 className="text-sm font-semibold">{modal ? 'Keep shopping' : 'Similar listings'}</h2>
				<p className="mt-1 truncate text-xs text-muted-foreground" translate="no">{modal ? `More from ${collection.name || collection.slug}` : collection.name || collection.slug}</p>
			</div>
			<Link href={collectionUrl} className={styles.viewAll} onClick={onNavigate}><span>View all</span><ArrowUpRight size={14} strokeWidth={1.7} aria-hidden /></Link>
		</div>
		{isLoading ? <div className={styles.grid} aria-label="Loading similar listings">{Array.from({ length: 3 }, (_, i) => <div key={i} className={styles.skeleton} />)}</div>
			: error && !data ? <div className={styles.empty}><p>Listings are temporarily unavailable.</p><button type="button" onClick={() => void mutate()}><RotateCw size={13} aria-hidden /><span>Try again</span></button></div>
			: <div className={styles.grid}>{[...(data?.listings || [])].sort((a, b) => Number(b.matches === 0) - Number(a.matches === 0)).map(({ item, matches }) => <div key={item.id} className={styles.card}>
				{/* The card opens the item; the button below buys it right here. */}
				<Link href={`/inscription/${item.id}?view=item`} className={styles.cardLink} aria-label={`View ${item.meta?.name || item.id}`} onClick={onNavigate}>
					<div className={styles.art}><InscriptionContent inscription={item} className="h-full w-full object-contain" smallCard /></div>
					<div className={styles.body}>
						<p className={styles.name} title={item.meta?.name} translate="no">{item.meta?.name || `#${item.num ?? item.id.slice(0, 8)}`}</p>
						<p className={styles.price} translate="no">{(Number(item.escrow?.satoshi_price) / 1e8).toFixed(4)}<span>BTC</span></p>
						<p className={styles.usd} translate="no">{exchangeRate ? `$${(Number(item.escrow?.satoshi_price) / 1e8 * exchangeRate).toLocaleString('en-US', { maximumFractionDigits: 0 })}` : '\u00a0'}{matches > 0 ? <span className={styles.traits}> · {matches} shared trait{matches === 1 ? '' : 's'}</span> : null}</p>
					</div>
				</Link>
				<div className={styles.actions}>
					<SimilarBuyNow item={item} onGone={() => void mutate()} />
				</div>
			</div>)}</div>}
	</section>
}
